import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DataProviderInstance } from '../../api/DataProvider';
import CreatePatientPopup from '../../popup/CreatePatientPopup/CreatePatientPopup';
import PageHeader from './components/PageHeader/PageHeader';
import SummaryCards from './components/SummaryCards/SummaryCards';
import PatientTable from './components/PatientTable/PatientTable';
import SidePanel from './components/SidePanel/SidePanel';
import RegistrationLinkPopup from './components/RegistrationLinkPopup/RegistrationLinkPopup';
import usePlanLock from '../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../consts/SubscriptionFeatures';
import usePatientsPage from './hooks/usePatientsPage';

/**
 * Patients - Main patients page
 *
 * Responsibilities:
 * - Asks the server for one page at a time (usePatientsPage): search and status
 *   run there, so a large account never downloads the whole list on this screen
 * - Manages create/edit popup state
 * - Renders PageHeader + SummaryCards + 2-column layout (PatientTable + SidePanel)
 * - Renders CreatePatientPopup when needed
 */
function Patients() {
  const { session } = DataProviderInstance();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 760px)');

  // Página, busca e status no servidor. LGPD: o servidor já deixa de fora o paciente
  // excluído (lápide), que o DataProvider mantém só para resolver nomes na agenda.
  const patientsPage = usePatientsPage(session?.currentCompany);

  // Popup state
  const [createPopupVisible, setCreatePopupVisible] = useState(false);
  const [selectedPatientData, setSelectedPatientData] = useState(null);
  const [registrationLinkVisible, setRegistrationLinkVisible] = useState(false);

  const registrationLinkLock = usePlanLock(
    SUBSCRIPTION_PERMISSIONS.PATIENT_REGISTRATION_LINK,
    'O link de cadastro do paciente'
  );

  // Handlers
  const handleNewPatient = () => {
    setSelectedPatientData(null);
    setCreatePopupVisible(true);
  };

  const handleEditPatient = (id) => {
    const patient = patientsPage.rows.find((p) => String(p.id) === String(id));
    setSelectedPatientData(patient || null);
    setCreatePopupVisible(true);
  };

  const handleViewPatient = (id) => {
    navigate(`/patients/${id}`);
  };

  const handleSchedulePatient = (id) => {
    // Navigate to appointments with patient pre-selected
    navigate(`/schedule/appointments?patientId=${id}`);
  };

  const handlePopupClose = () => {
    setCreatePopupVisible(false);
    setSelectedPatientData(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: '#F8F9FC',
        minHeight: '100vh',
        p: { xs: 2, md: 3 },
      }}
    >
      {/* Page Header: breadcrumb + title + "New patient" button */}
      <PageHeader
        onNewPatient={handleNewPatient}
        onRegistrationLink={registrationLinkLock.guard(() => setRegistrationLinkVisible(true))}
        registrationLinkLocked={registrationLinkLock.locked}
      />

      {/* Summary Cards: 4 KPI cards */}
      <SummaryCards totalPatients={patientsPage.totals.all} activePatients={patientsPage.totals.active} />

      {/* Main content: 2-column layout */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          mt: 3,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'flex-start',
        }}
      >
        {/* Left: Patient Table */}
        <Box sx={{ flex: 1, minWidth: 0 }} data-tour="patients-list">
          <PatientTable
            patients={patientsPage.rows}
            totalItems={patientsPage.total}
            isLoading={patientsPage.isLoading}
            filter={patientsPage.search}
            statusFilter={patientsPage.status}
            currentPage={patientsPage.page}
            rowsPerPage={patientsPage.rowsPerPage}
            onFilterChange={patientsPage.setSearch}
            onStatusFilterChange={patientsPage.setStatus}
            onPageChange={patientsPage.setPage}
            onRowsPerPageChange={patientsPage.setRowsPerPage}
            onViewPatient={handleViewPatient}
            onSchedulePatient={handleSchedulePatient}
            onEditPatient={handleEditPatient}
          />
        </Box>

        {/* Right: Side Panel */}
        <Box
          sx={{
            width: isMobile ? '100%' : '280px',
            flexShrink: 0,
          }}
        >
          <SidePanel totalPatients={patientsPage.totals.all} />
        </Box>
      </Box>

      {/* Create / Edit Patient Popup */}
      <CreatePatientPopup
        Data={selectedPatientData}
        Visibility={createPopupVisible}
        OnHide={handlePopupClose}
        OnChange={patientsPage.reload}
      />

      {registrationLinkVisible && (
        <RegistrationLinkPopup onClose={() => setRegistrationLinkVisible(false)} />
      )}
      {registrationLinkLock.modal}
    </Box>
  );
}

export default Patients;
